import React, { useCallback, useEffect, useRef, useState } from "react";

function formatSize(bytes) {
  if (!bytes || bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Non-dismissible upload modal for Sitecore Experience Forms with file
 * attachments. Driven by custom events from Form.cshtml (thread:form-upload:*).
 * Shows real browser-to-server upload progress; after 100% switches to a
 * processing state until the form AJAX call completes.
 */
const SitecoreFormUploadModal = () => {
  const [open, setOpen] = useState(false);
  const [percent, setPercent] = useState(0);
  const [hasProgress, setHasProgress] = useState(false);
  const [phase, setPhase] = useState("uploading");
  const [fileCount, setFileCount] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);

  // Tracks whether THIS modal actually opened the upload flow. Used so cleanup
  // (resetting body overflow, the global active flag, etc.) only runs when we
  // own that state. Without this guard, a "complete" event from any unrelated
  // Sitecore form AJAX call (e.g. multi-step navigation) would reset body
  // overflow and could break another component's scroll lock (e.g. GatedContent).
  const openRef = useRef(false);

  const close = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;

    setOpen(false);
    setPercent(0);
    setHasProgress(false);
    setPhase("uploading");
    setFileCount(0);
    setTotalBytes(0);
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    window.__threadFormUploadActive = false;
  }, []);

  useEffect(() => {
    const onStart = (e) => {
      const detail = e.detail || {};
      setFileCount(detail.fileCount || 0);
      setTotalBytes(detail.totalBytes || 0);
      setPercent(0);
      setHasProgress(false);
      setPhase("uploading");
      openRef.current = true;
      setOpen(true);
      window.__threadFormUploadActive = true;
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    };

    const onProgress = (e) => {
      const p = e.detail?.percent ?? 0;
      setHasProgress(true);
      setPercent(Math.min(100, Math.max(0, p)));
    };

    const onProcessing = () => {
      setPhase("processing");
      setPercent(100);
    };

    const onComplete = () => close();

    window.addEventListener("thread:form-upload:start", onStart);
    window.addEventListener("thread:form-upload:progress", onProgress);
    window.addEventListener("thread:form-upload:processing", onProcessing);
    window.addEventListener("thread:form-upload:complete", onComplete);

    const blockEscape = (e) => {
      if (!window.__threadFormUploadActive) return;
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", blockEscape, true);

    return () => {
      window.removeEventListener("thread:form-upload:start", onStart);
      window.removeEventListener("thread:form-upload:progress", onProgress);
      window.removeEventListener("thread:form-upload:processing", onProcessing);
      window.removeEventListener("thread:form-upload:complete", onComplete);
      document.removeEventListener("keydown", blockEscape, true);
    };
  }, [close]);

  if (!open) return null;

  const fileLabel = fileCount === 1 ? "1 file" : `${fileCount} files`;
  const sizeText = formatSize(totalBytes);
  const isProcessing = phase === "processing";

  // While uploading, show a real % bar only once progress data has arrived;
  // otherwise (and during server processing) show an animated indeterminate bar
  // so it never looks frozen.
  const isDeterminate = !isProcessing && hasProgress;

  const title = isProcessing
    ? "Finishing your submission"
    : "Uploading your files";

  const detail = isProcessing
    ? `Processing ${fileLabel} (${sizeText}) on the server. Please wait and do not close this page.`
    : `Uploading ${fileLabel} (${sizeText}). Please wait and do not close this page.`;

  let statusText;
  if (isProcessing) {
    statusText = "Processing...";
  } else if (isDeterminate) {
    statusText = `${percent}% complete`;
  } else {
    statusText = "Starting upload...";
  }

  return (
    <div
      className="sitecore-form-upload-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="sitecore-form-upload-title"
      aria-describedby="sitecore-form-upload-detail"
      aria-busy="true"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="sitecore-form-upload-modal__dialog">
        <div className="sitecore-form-upload-modal__icon" aria-hidden="true">
          <span className="sitecore-form-upload-modal__icon-ring" />
          <span className="sitecore-form-upload-modal__icon-core" />
        </div>
        <h2
          id="sitecore-form-upload-title"
          className="sitecore-form-upload-modal__title"
        >
          {title}
        </h2>
        <p
          id="sitecore-form-upload-detail"
          className="sitecore-form-upload-modal__detail"
        >
          {detail}
        </p>

        <div
          className="sitecore-form-upload-modal__progress-track"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={isDeterminate ? percent : undefined}
          aria-label={isProcessing ? "Processing upload" : "Upload progress"}
        >
          <div
            className={`sitecore-form-upload-modal__progress-bar${
              isDeterminate ? "" : " is-indeterminate"
            }`}
            style={isDeterminate ? { width: `${percent}%` } : undefined}
          />
        </div>

        <p className="sitecore-form-upload-modal__status" aria-live="polite">
          {statusText}
        </p>
      </div>
    </div>
  );
};

export default SitecoreFormUploadModal;
